import { useRef, useState } from 'react'
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader
} from '@heroui/react'
import { usePostHog } from '@posthog/react'
import { useTheme } from '@/ui/theme'
import { useTranslate } from '@/hooks'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { setExchangeAddress } from '@/redux/return/return-slice'
import { EditPencilIcon } from '@/ui/icons/edit-pencil'
import { AddressForm, AddressFormData } from '../return-address/address-form'

export const ShippingAddress = () => {
  const { translate } = useTranslate()

  const theme = useTheme()

  const order = useAppSelector((store) => store.returnsApi.order.response)

  const posthog = usePostHog()

  const exchangeAddress = useAppSelector(
    (store) => store.return.exchangeAddress
  )

  const dispatch = useAppDispatch()

  const [isOpen, setIsOpen] = useState(false)
  const drawerRef = useRef<HTMLDivElement>(null)

  const onSubmit = (values: AddressFormData) => {
    const addressData = {
      ...values,
      country: values.country?.name,
      country_code: values.country?.code,
      state_province: values.province?.name,
      state_province_code: values.province?.code
    }

    posthog.capture('Shipping Address Edited', {
      checkout_id: order.checkout_id
    })

    dispatch(setExchangeAddress(addressData))
    setIsOpen(false)
  }

  const color = theme.colors.primary.main
  const textColor = theme.colors.primary.contrastText

  return (
    <>
      <div className="space-y-3">
        <h2
          className="text-base font-medium"
          dangerouslySetInnerHTML={{
            __html: translate('confirmation_page_shipping_address_title')
          }}
        />
        <div className="flex items-start justify-between rounded-md border border-solid border-neutral-200 px-4 py-4">
          <div className="flex flex-col gap-0.5">
            <p className="flex-1 text-sm font-medium">
              {exchangeAddress?.first_name + ' ' + exchangeAddress?.last_name}
            </p>
            <p className="shrink-0 text-sm font-normal text-neutral-500">
              {exchangeAddress?.address_line_1}, {exchangeAddress?.postcode}{' '}
              {exchangeAddress?.city}, {exchangeAddress?.country}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              posthog.capture('Shipping Address Edit Button Clicked', {
                checkout_id: order.checkout_id
              })
              setIsOpen(true)
            }}
            className="ml-auto flex shrink-0 items-center gap-1 p-0.5 text-xs font-normal text-neutral-500 transition-colors hover:text-neutral-700"
          >
            <EditPencilIcon className="size-3.5" />
            {translate('confirmation_page_edit_shipping_address_button')}
          </button>
        </div>
      </div>
      <Drawer
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        backdrop="blur"
        radius="sm"
        classNames={{
          backdrop: 'backdrop-blur-[2px] bg-black/10',
          base: 'rounded-none md:rounded-md md:right-2 md:top-2 md:bottom-2 w-full max-w-full md:max-w-[500px]'
        }}
      >
        <DrawerContent>
          {() => (
            <>
              <DrawerHeader
                className="flex flex-col gap-1"
                dangerouslySetInnerHTML={{
                  __html: translate('confirmation_page_shipping_address_title')
                }}
              />
              <DrawerBody className="flex flex-col px-6 pb-6">
                <div ref={drawerRef} className="flex flex-1 flex-col gap-4">
                  <AddressForm
                    address={exchangeAddress}
                    onSubmit={onSubmit}
                    portalContainer={drawerRef}
                  />
                  <Button
                    form="address"
                    type="submit"
                    fullWidth
                    radius="sm"
                    size="md"
                    className="mt-auto text-sm"
                    style={{
                      backgroundColor: color,
                      color: textColor,
                      borderRadius: theme.borderRadius
                    }}
                  >
                    {translate(
                      'confirmation_page_save_shipping_address_button'
                    )}
                  </Button>
                </div>
              </DrawerBody>
            </>
          )}
        </DrawerContent>
      </Drawer>
    </>
  )
}
